import React, { useEffect, useState } from 'react';
import './_NewRiderService.css';
import { RiderServicesModel } from '../models/RiderServicesModel';
import {
    RiderServiceModel,
    RiderServiceCompanyModel,
    RiderComapnyModel,
    RiderServiceBranch,
} from '../models/superbit-api-models';

// React port of Views/SystemManagement/Riders/_NewRiderService.cshtml and
// _UpdateRiderService.cshtml (identical forms) plus the addRiderServiceBtn submit
// logic from wwwroot/js/systemManagement.js. Handles BOTH add (serviceId undefined)
// and edit (serviceId set); both save through riderUpsertService, exactly like the
// old form which always posted to Rider/UpsertService. The company inputs the form
// binds to come back as AllComapnies on the RiderServiceModel from riderGetService.

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

type Props = {
    show: boolean;
    // Existing service id when editing; undefined when adding.
    serviceId?: string;
    onClose: () => void;
    onSaved: () => void;
};

// Per-company input state. broadcastCode is the editable value; itemId is the
// existing RiderServiceCompany.Id (EMPTY_GUID for a new one) so an edit updates
// in place instead of creating a duplicate — mirroring the old form's itemId attr.
type CompanyInput = {
    companyId: string;
    name: string;
    broadcastCode: string;
    itemId: string;
};

const NewRiderService = (props: Props) => {
    const { show, serviceId, onClose, onSaved } = props;
    const isEdit = !!serviceId;

    const [servicesModel] = useState<RiderServicesModel>(() => new RiderServicesModel());

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [serverError, setServerError] = useState<string>('');
    const [error, setError] = useState<string>('');

    // --- form fields ---
    const [title, setTitle] = useState<string>('');
    const [riderServiceBranch, setRiderServiceBranch] = useState<RiderServiceBranch>(
        RiderServiceBranch._0
    );
    const [serviceReporterLink, setServiceReporterLink] = useState<string>('');
    const [companyInputs, setCompanyInputs] = useState<CompanyInput[]>([]);

    useEffect(() => {
        if (!show) return;
        setError('');
        setServerError('');
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, serviceId]);

    const load = async () => {
        setLoading(true);
        try {
            const model = await servicesModel.getService(serviceId);

            setTitle(model.title || '');
            setRiderServiceBranch(model.riderServiceBranch ?? RiderServiceBranch._0);
            setServiceReporterLink(model.serviceReporterLink || '');

            // One input per company in AllComapnies, seeded from any existing
            // RiderServiceCompany for that company (the old cshtml's per-company lookup).
            const existing = model.riderServiceCompanies || [];
            const inputs: CompanyInput[] = (model.allComapnies || []).map(
                (company: RiderComapnyModel) => {
                    const item = existing.find((x) => x.riderComapnyId === company.id);
                    return {
                        companyId: company.id || '',
                        name: company.name || '',
                        broadcastCode: (item && item.broadcastCode) || '',
                        itemId: (item && item.id) || EMPTY_GUID,
                    };
                }
            );
            setCompanyInputs(inputs);
        } catch (e) {
            console.error('Failed to load rider service', e);
            setServerError('טעינת השירות נכשלה');
        } finally {
            setLoading(false);
        }
    };

    const updateCompany = (companyId: string, broadcastCode: string) => {
        setCompanyInputs((prev) =>
            prev.map((c) => (c.companyId === companyId ? { ...c, broadcastCode } : c))
        );
        if (error) setError('');
    };

    const onSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
        if (e && typeof (e as any).preventDefault === 'function') e.preventDefault();
        setServerError('');

        // The old form requires at least one company broadcast code to be filled in.
        const anyCode = companyInputs.some((c) => c.broadcastCode.trim() !== '');
        if (!anyCode) {
            setError('יש לבחור ספק שירות');
            return;
        }

        const body = new RiderServiceModel();
        body.id = serviceId || EMPTY_GUID;
        body.title = title.trim();
        body.riderServiceBranch = riderServiceBranch;
        body.serviceReporterLink = serviceReporterLink || undefined;

        // Send an entry for every company input (matching the old serialize loop),
        // so clearing a code on edit persists too. itemId keeps existing rows updating
        // in place; EMPTY_GUID marks a new one.
        body.riderServiceCompanies = companyInputs.map((c) => {
            const rsc = new RiderServiceCompanyModel();
            rsc.id = c.itemId || EMPTY_GUID;
            rsc.riderComapnyId = c.companyId;
            rsc.broadcastCode = c.broadcastCode;
            return rsc;
        });

        setSaving(true);
        try {
            await servicesModel.upsertService(body);
            onSaved();
            onClose();
        } catch (err) {
            console.error('Failed to save rider service', err);
            setServerError('שמירת השירות נכשלה');
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="riderservice-overlay" onClick={handleOverlayClick}>
            <div className="riderservice-modal" dir="rtl">
                <form id="addRiderServiceForm" autoComplete="off" onSubmit={onSubmit}>
                    <div className="header">
                        <button type="button" className="close" aria-label="Close" onClick={onClose}>
                            <span className="close-modal" />
                        </button>
                        <h3 className="title">שירות Rider</h3>
                    </div>

                    {loading ? (
                        <div className="riderservice-loading">טוען…</div>
                    ) : (
                        <>
                            {/* Row 1: service name + branch */}
                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">שם השירות</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                    />
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">ענף</span>
                                    </div>
                                    <select
                                        className="form-control"
                                        value={riderServiceBranch}
                                        onChange={(e) =>
                                            setRiderServiceBranch(
                                                parseInt(e.target.value, 10) as RiderServiceBranch
                                            )
                                        }
                                    >
                                        <option value={RiderServiceBranch._0}>רכב</option>
                                        <option value={RiderServiceBranch._1}>דירה</option>
                                    </select>
                                </div>
                            </div>

                            {/* File link (ServiceReporterLink). The old form also had an
                                "upload file" button wired to a separate generic upload
                                endpoint; the link field is the bound value and is kept here. */}
                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">קישור לקובץ</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        value={serviceReporterLink}
                                        onChange={(e) => setServiceReporterLink(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* One broadcast-code input per rider company. */}
                            <div className="riderservice-companies">
                                {companyInputs.map((c) => (
                                    <div className="col-sm compCol" key={c.companyId}>
                                        <div className="minor-title-box text-right">
                                            <span className="title">{c.name}</span>
                                        </div>
                                        <input
                                            className="form-control riderServiceComany"
                                            value={c.broadcastCode}
                                            onChange={(e) => updateCompany(c.companyId, e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>

                            {error && <div className="riderservice-err">{error}</div>}
                            {serverError && <div className="riderservice-err">{serverError}</div>}

                            <div className="action-buttons">
                                <div className="action-buttons__half">
                                    <button
                                        type="button"
                                        className="btn btn-link button btn-primary button-outlined"
                                        disabled={saving}
                                        onClick={onSubmit}
                                    >
                                        {saving ? 'שומר…' : isEdit ? 'עדכן' : 'הוסף'}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-link button button-outlined"
                                        onClick={onClose}
                                    >
                                        ביטול
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default NewRiderService;
